/// FOURJS_START_COPYRIGHT(D,2014)
/// Property of Four Js*
/// (c) Copyright Four Js 2014, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("NotificationsApplicationService", ["ApplicationServiceBase", "ApplicationServiceFactory"], function (context, cls) {
  class NotificationsApplicationService extends cls.ApplicationServiceBase /** @lends gbc.NotificationsApplicationService */ {
    /**
     * @readonly
     * @enum{string}
     */
    InteractionType = {
      selected: "selected",
      deleted: "deleted"
    };

    /**
     * Holds pending notification
     * @type {Map<number, Notification>}
     * @private
     */
    _currentNotifications = null;

    /**
     * Maps a notification id to a user action
     * @type {Map<number, InteractionType>}
     * @private
     */
    _lastInteractions = null;

    /**
     * When creating a new notification, if no id is provided by the user,
     * this is used to generate a new one
     * @type {number}
     * @private
     */
    _highestId = 0;

    constructor(app) {
      super(app);
      this._currentNotifications = new Map();
      this._lastInteractions = new Map();
    }

    /**
     * Requests browser permission to use the Notification API
     * @returns {Promise<boolean>}
     */
    async getPermission() {
      if (!("Notification" in globalThis)) {
        console.warn("This browser does not support notifications.");
        return false;
      }
      const permission = await Notification.requestPermission();
      return permission === "granted";
    }

    /**
     * Creates a local notification
     * @param id - the id the new notification should be identified with
     * if null, one will be generated
     * @param title
     * @param body - the content of the notification
     * @param icon
     * @returns {Promise<number>}
     * -number : the id of the new notification
     * --1: if the given id was invalid (already existing, negative)
     */
    async createNotification(id, title, body, icon) {
      if ((id && id < 0) || !title || this._currentNotifications.has(id)) {
        return -1;
      }
      await this.getPermission();
      if (Notification?.permission !== "granted") {
        throw new Error("You do not have permission to use notifications");
      }

      // If no id is provided, we generate one
      const newId = id === null || id === undefined ? this._highestId + 1 : id;
      this._highestId = Math.max(newId, this._highestId);

      const notification = new Notification(title, { body: body, icon: icon });
      notification.onclick = () => {
        this._lastInteractions.set(newId, this.InteractionType.selected);
        const app = context.SessionService.getCurrent().getCurrentApplication();
        app.scheduler.nativeNotificationSelectedCommand();
        window.focus(); // To focus on the correct tab
        this._clearNotification(newId, notification, false);
      };
      notification.onclose = () => {
        this._lastInteractions.set(newId, this.InteractionType.deleted);
      };
      this._currentNotifications.set(newId, notification);
      return newId;
    }

    /**
     * Removes a notification and its eventual recorded interaction
     * @param id
     * @param notification
     * @param removeLastInteraction - false to keep the recorded "selected" or "closed"
     * @private
     */
    _clearNotification(id, notification, removeLastInteraction = true) {
      notification.onclose = null; // Don't trigger the push to last interactions
      notification.close();
      if (removeLastInteraction) {
        this._lastInteractions.delete(id);
      }
      this._currentNotifications.delete(id);
    }

    /**
     * Removes notifications from the system's notification center
     * @param ids - the ids of notifications to remove, if null or empty all will be removed
     * @returns {number[]}
     * -number[]: list of removed ids
     */
    clearNotifications(ids) {
      const clearedNotifs = [];
      if (ids?.length > 0) {
        for (const id of ids) {
          const notification = this._currentNotifications.get(id);
          if (!notification) {
            continue;
          }
          this._clearNotification(id, notification);
          clearedNotifs.push(id);
        }
      } else {
        // No id provided: clear all notifications
        for (const [id, notification] of Array.from(this._currentNotifications)) {
          this._clearNotification(id, notification);
          clearedNotifs.push(id);
        }
      }
      return clearedNotifs;
    }

    /**
     * Gets all user actions made on notifications since the last call to this function
     * Does not include actions made programmatically (like clearNotifications)
     * @returns {Array.<Object.<number, string>>}
     */
    popLastInteractions() {
      const interactions = [...this._lastInteractions];
      this._lastInteractions.clear();
      return interactions;
    }

    /**
     * @inheritDoc
     */
    destroy() {
      this.clearNotifications();
    }
  }

  cls.NotificationsApplicationService = NotificationsApplicationService;

  cls.ApplicationServiceFactory.register("Notifications", cls.NotificationsApplicationService);
});
